import { useNavigate } from "react-router";
import { CardReceive, CardSend, CardPos, ArrowSwapHorizontal } from "iconsax-react";
import cn from "classnames";
import { Button } from "@/shared/ui";
import { useAppDispatch } from "@/shared/hooks";
import { FINANCE_PAGE_ROUTE, type Locale } from "@/shared/utils";
import { setActiveProject } from "@/features/settings";
import type { BudgetSummary, Project } from "../../../types";
import { formatBudgetCompact } from "../../../utils";
import SectionCard from "./SectionCard";

interface BudgetSectionProps {
  summary: BudgetSummary;
  project: Project;
  projectId: number;
  locale: Locale;
  t: (key: string, params?: Record<string, unknown>) => string;
}

const CATEGORY_BORDER_COLORS = {
  admin: "background-info-fill",
  mto: "background-positive-fill",
  direct: "background-negative-fill",
} as const;

const DONUT_COLORS: Record<string, string> = {
  admin: "var(--color-blue-500)",
  mto: "var(--color-green-500)",
  direct: "var(--color-red-500)",
};

export default function BudgetSection({ summary, project, projectId, locale, t }: BudgetSectionProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const hasSmeta = project.budget !== null;
  const canRedistribute =
    !project.is_internal && hasSmeta && (project.status === "active" || project.status === "planned");

  function handleRedistribute() {
    navigate(`${FINANCE_PAGE_ROUTE}?redistribute=${project.id}`);
  }

  const categories = [
    { key: "admin" as const, label: t("detail.budgetAdmin"), data: summary.categories.admin },
    { key: "mto" as const, label: t("detail.budgetMto"), data: summary.categories.mto },
    { key: "direct" as const, label: t("detail.budgetDirect"), data: summary.categories.direct },
  ];

  const totalBudget = parseFloat(summary.total_budget) || 0;

  function handleNavigateToFinance() {
    dispatch(setActiveProject(projectId));
    navigate(FINANCE_PAGE_ROUTE);
  }

  const statCards = [
    {
      icon: <CardReceive size={16} variant="Bold" color="currentColor" />,
      colors: "background-info-subtle content-action-info",
      label: t("detail.totalBudget"),
      value: formatBudgetCompact(summary.total_budget, locale),
      subtitle: t("detail.budgetApproved"),
    },
    {
      icon: <CardSend size={16} variant="Bold" color="currentColor" />,
      colors: "background-positive-subtle content-action-positive",
      label: t("detail.totalSpent"),
      value: formatBudgetCompact(summary.total_spent, locale),
      subtitle: t("detail.spentPercent", { value: summary.spent_percentage.toFixed(0) }),
    },
    {
      icon: <CardPos size={16} variant="Bold" color="currentColor" />,
      colors: "background-notice-subtle content-action-notice",
      label: t("detail.remaining"),
      value: formatBudgetCompact(summary.remaining, locale),
      subtitle: t("detail.remainingPercent", { value: (100 - summary.spent_percentage).toFixed(0) }),
    },
  ];

  const size = 200;
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const gapAngle = 0.06;

  const visibleCategories = categories.filter((cat) => (parseFloat(cat.data.amount) || 0) > 0);
  const totalGapAngle = visibleCategories.length * gapAngle;
  const availableAngle = 2 * Math.PI - totalGapAngle;

  let currentAngle = -Math.PI / 2 + gapAngle / 2;

  function describeArc(startAngle: number, endAngle: number): string {
    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  }

  if (!hasSmeta) {
    return (
      <SectionCard
        title={t("detail.budgetSection")}
        subtitle={t("detail.budgetSubtitle")}
        link={FINANCE_PAGE_ROUTE}
        onLinkClick={handleNavigateToFinance}>
        <div className="flex items-center justify-center py-10">
          <p className="text-body-regular-md content-base-secondary">{t("detail.noSmeta")}</p>
        </div>
      </SectionCard>
    );
  }

  return (
    <>
    <SectionCard
      title={t("detail.budgetSection")}
      subtitle={t("detail.budgetSubtitle")}
      link={FINANCE_PAGE_ROUTE}
      onLinkClick={handleNavigateToFinance}>
      <div className="grid grid-cols-3 gap-4 mb-6">
        {statCards.map((card) => (
          <div key={card.label} className="flex flex-col gap-4 p-4 radius-md border surface-component-stroke">
            <div className="flex items-center gap-2">
              <div className={cn("w-8 h-8 radius-xs flex shrink-0 items-center justify-center", card.colors)}>
                {card.icon}
              </div>
              <span className="text-label-sm content-base-primary">{card.label}</span>
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-display-sm content-base-primary">{card.value}</p>
              <span className="text-body-regular-xs content-base-low">{card.subtitle}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="radius-lg border surface-base-stroke p-5">
        <div className="flex gap-5 items-start">
          <div className="flex-1 flex flex-col gap-3">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 radius-xs flex shrink-0 items-center justify-center background-info-subtle content-action-info">
                <CardReceive size={16} variant="Bold" color="currentColor" />
              </div>
              <p className="text-label-sm content-base-primary">{t("detail.budgetBreakdown")}</p>
            </div>
            <div className="flex flex-col gap-3">
              {categories.map((cat) => (
                <div key={cat.key} className="flex justify-between items-center gap-4">
                  <div className="flex justify-start items-center gap-1.5">
                    <span className={cn("w-1 h-5 rounded-full", CATEGORY_BORDER_COLORS[cat.key])}></span>
                    <p className="text-body-regular-sm content-action-neutral">{cat.label}</p>
                  </div>

                  <span className="text-body-bold-md content-base-primary">
                    {formatBudgetCompact(cat.data.amount, locale)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="shrink-0 w-80 h-50 flex justify-center">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="w-50 h-50">
              {visibleCategories.map((cat) => {
                const value = parseFloat(cat.data.amount) || 0;
                const pct = totalBudget > 0 ? value / totalBudget : 0;
                const sweepAngle = pct * availableAngle;
                const startAngle = currentAngle;
                const endAngle = currentAngle + sweepAngle;
                currentAngle = endAngle + gapAngle;

                return (
                  <path
                    key={cat.key}
                    d={describeArc(startAngle, endAngle)}
                    fill="none"
                    stroke={DONUT_COLORS[cat.key] || "var(--color-grey-400)"}
                    strokeWidth={strokeWidth}
                    className="rounded-md"
                  />
                );
              })}
              <text x={cx} y={cy - 4} textAnchor="middle" className="text-heading-md content-base-primary">
                {formatBudgetCompact(summary.total_budget, locale)}
              </text>
              <text x={cx} y={cy + 16} textAnchor="middle" className="text-body-regular-xs content-base-secondary">
                {t("detail.budgetTotal")}
              </text>
            </svg>
          </div>
        </div>
      </div>

      {canRedistribute && (
        <div className="flex justify-end mt-4">
          <Button size="lg" variant="primary" onClick={handleRedistribute}>
            <span className="flex items-center gap-2">
              <ArrowSwapHorizontal size={20} color="currentColor" />
              {t("projectForm.redistribute.button")}
            </span>
          </Button>
        </div>
      )}
    </SectionCard>
    </>
  );
}

